/** Autenticação JWT — delega para services/auth.service.ts */

import type { PainelRole, PainelPermission } from '@/lib/painel-rbac';
import * as authService from '@/services/auth.service';
import { analytics, AnalyticsEventNames } from '@/services/analytics';
import { ensureTokenRefreshScheduled } from '@/services/api-client';

export const ADMIN_AUTH_STORAGE_KEY = 'xat_jwt_token';
export const ADMIN_SESSION_STORAGE_KEY = 'xat_jwt_session';
export const ADMIN_PANEL_VERSION = 'v2.0 (Preview)';
export const ADMIN_LOGIN_PATH = authService.ADMIN_LOGIN_PATH;
export const ADMIN_DASHBOARD_PATH = authService.ADMIN_DASHBOARD_PATH;

export type AdminSession = authService.AdminSession & {
  permissions?: PainelPermission[];
};

let cachedSession: AdminSession | null = null;

function getRememberPreference(): boolean {
  if (typeof window === 'undefined') return true;
  const raw = window.localStorage.getItem('xat_jwt_remember');
  if (raw === null) return true;
  return raw !== '0';
}

function sessionStore(): Storage {
  return getRememberPreference() ? window.localStorage : window.sessionStorage;
}

function readStoredSession(): AdminSession | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw =
      window.localStorage.getItem(ADMIN_SESSION_STORAGE_KEY) ??
      window.sessionStorage.getItem(ADMIN_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdminSession;
  } catch {
    return null;
  }
}

function persistSession(session: AdminSession | null): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
  window.sessionStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
  if (session) {
    sessionStore().setItem(ADMIN_SESSION_STORAGE_KEY, JSON.stringify(session));
  }
}

export function getAdminSession(): AdminSession | null {
  if (cachedSession) return cachedSession;
  const stored = readStoredSession();
  if (stored) {
    cachedSession = stored;
    return stored;
  }
  return null;
}

export function setCachedAdminSession(session: AdminSession | null): void {
  cachedSession = session;
  persistSession(session);
}

export function isAdminAuthenticated(): boolean {
  return authService.isAuthenticated();
}

export function clearAdminAuth(): void {
  void analytics.cms(AnalyticsEventNames.CMS_LOGOUT, {});
  setCachedAdminSession(null);
  authService.logout().catch(() => undefined);
}

export function adminLogout(): void {
  clearAdminAuth();
  if (typeof window !== 'undefined') {
    window.location.href = ADMIN_LOGIN_PATH;
  }
}

export type AdminLoginResult =
  | { ok: true; session: AdminSession }
  | { ok: false; message: string };

export async function attemptAdminLogin(
  username: string,
  password: string,
  remember = true
): Promise<AdminLoginResult> {
  try {
    const session = await authService.login(username, password, remember);
    setCachedAdminSession(session);
    void analytics.cms(AnalyticsEventNames.CMS_LOGIN, { userId: session.username });
    return { ok: true, session };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Usuário ou senha incorretos.';
    return { ok: false, message };
  }
}

export async function hydrateAdminSession(): Promise<AdminSession | null> {
  if (!isAdminAuthenticated()) {
    setCachedAdminSession(null);
    return null;
  }
  const session = await authService.me();
  setCachedAdminSession(session);
  if (session) {
    ensureTokenRefreshScheduled();
  }
  return session;
}

export type { PainelRole };
